import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { formatNormalDate } from '@util/string';
import store from '@store/store';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';

// ###################################
// iyagigo path
// ###################################
export const IYAGIGO_PATH = {
  // ############### landing path ###############
  ROOT_PATH: '/',
  LANDING_PATH: '/ig',
  APP_PATH: '/ig/app',
  INITIAL_SERVICE_PATH: '/ig/home/begin',
  BRIDGE_PATH: '/ig/bridge',
  // ############### study plan path (첫 학습 전 학습 계획) ###############
  STUDY_PLAN_PATH: '/ig/study-plan',
  // ############### home path (Hub — 4기능 통합 진입점) ###############
  HOME_PATH: '/ig/home/:home',
  HOME_BEGIN_PATH: '/ig/home/begin',
  WORKSHEET_PATH: '/ig/worksheet',
  // ############### classroom path (일본어교실 — 글자/기초문법 입문) ###############
  CLASSROOM_PATH: '/ig/classroom/:classroom',
  CLASSROOM_HOME_PATH: '/ig/classroom/home',
  CLASSROOM_CHARACTER_PATH: '/ig/classroom/character',
  CLASSROOM_EXPRESSION_PATH: '/ig/classroom/expression',
  CLASSROOM_CONVERSATION_PATH: '/ig/classroom/conversation',
  CLASSROOM_GRAMMAR_PATH: '/ig/classroom/grammar',
  // ############### speaking path (AI 말하기) ###############
  SPEAKING_PATH: '/ig/speaking/:speaking',
  SPEAKING_HOME_PATH: '/ig/speaking/home',
  SPEAKING_SESSION_PATH: '/ig/speaking/session/:sessionKey',
  // ############### clip path (일상표현 — 생생표현/간편이야기) ###############
  CLIP_PATH: '/ig/clip/:clip',
  CLIP_HOME_PATH: '/ig/clip/home',
  CLIP_AUTHENTIC_EXPRESSION_PATH: '/ig/clip/authentic-expression',
  CLIP_MINI_STORY_PATH: '/ig/clip/mini-story',
  // ############### cloze path ###############
  CLOZE_PATH: '/ig/cloze/:cloze',
  CLOZE_HOME_PATH: '/ig/cloze/home',
  CLOZE_STUDY_PATH: '/ig/cloze/study',
  CLOZE_NOTE_PATH: '/ig/cloze/note',
  // ############### vocabulary-book path ###############
  VOCABULARY_BOOK_PATH: '/ig/vocabulary-book/:vocabularyBook',
  VOCABULARY_BOOK_HOME_PATH: '/ig/vocabulary-book/home',
  VOCABULARY_BOOK_STUDY_PATH: '/ig/vocabulary-book/study',
  VOCABULARY_BOOK_REVIEW_PATH: '/ig/vocabulary-book/review',
  VOCABULARY_BOOK_NOTE_PATH: '/ig/vocabulary-book/note',
  // ############### test path ###############
  TEST_PATH: `/ig/test/:test`,
  TEST_HOME_PATH: `/ig/test/home`,
  TEST_PROBLEM_PATH: `/ig/test/problem`,
  // ############### hub path ###############
  HUB_PATH: '/ig/hub/:hub',
  HUB_HOME_PATH: '/ig/hub/home',
  HUB_HELP_PATH: '/ig/hub/help',
  HUB_CONTACT_PATH: '/ig/hub/contact',
  HUB_APPLICATION_METADATA_PATH: '/ig/hub/application-metadata',
  HUB_ACCOUNT_PATH: '/ig/hub/account',
  HUB_SUBSCRIPTION_PATH: '/ig/hub/subscription',
  // ############### studio path ###############
  STUDIO_PATH: '/ig/studio/:studio',
  STUDIO_HOME_PATH: '/ig/studio/home',
  STUDIO_CONFIGURATION_PATH: '/ig/studio/configuration',
  STUDIO_STATISTIC_PATH: '/ig/studio/statistic',
};

// ############### home path ###############
export const createHomePath = (page: 'begin' = 'begin') => {
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu ||
    null) as TIyagigoSubMenu['home'];
  if (subMenu) return `/ig/home/${subMenu}`;
  return `/ig/home/${page}`;
};

// ############### classroom path ###############
export const createClassroomPath = (page: 'home' | 'sub' = 'sub') => {
  if (page === 'home') return IYAGIGO_PATH.CLASSROOM_HOME_PATH;
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu ||
    null) as TIyagigoSubMenu['classroom'];
  const studyLanguage = store.getState().rootReducer?.studentReducer?.currentStudyLanguage ?? null;
  const language = (studyLanguage ?? EApiLanguageCode.JAPANESE).toLowerCase();
  if (subMenu) {
    return `/ig/classroom/${subMenu}?language=${language}`;
  }

  return IYAGIGO_PATH.CLASSROOM_HOME_PATH;
};

// ############### speaking path (AI 말하기) ###############
export const createSpeakingPath = (page: 'home' | 'session' = 'home', sessionKey?: string) => {
  if (page === 'session' && sessionKey) return `/ig/speaking/session/${encodeURIComponent(sessionKey)}`;
  return IYAGIGO_PATH.SPEAKING_HOME_PATH;
};

// ############### clip path (일상표현 — 생생표현/간편이야기) ###############
export const createClipPath = (page: 'home' | 'sub' = 'sub') => {
  if (page === 'home') return IYAGIGO_PATH.CLIP_HOME_PATH;
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu ||
    null) as TIyagigoSubMenu['clip'];
  const studyLanguage = store.getState().rootReducer?.studentReducer?.currentStudyLanguage ?? null;
  const language = (studyLanguage ?? EApiLanguageCode.JAPANESE).toLowerCase();
  if (subMenu) {
    return `/ig/clip/${subMenu}?language=${language}`;
  }

  return IYAGIGO_PATH.CLIP_HOME_PATH;
};

// ############### cloze path ###############
export const createClozePath = (page: 'home' | 'study' | 'note') => {
  if (page === 'home') return IYAGIGO_PATH.CLOZE_HOME_PATH;
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu ||
    null) as TIyagigoSubMenu['cloze'];
  const studyLanguage = store.getState().rootReducer?.studentReducer?.currentStudyLanguage ?? null;
  const language = (studyLanguage ?? EApiLanguageCode.JAPANESE).toLowerCase();
  if (subMenu) return `/ig/cloze/${page}?language=${language}&subject=${subMenu}`;
  return IYAGIGO_PATH.CLOZE_HOME_PATH;
};

// ############### vocabulary-book path ###############
export const createVocabularyBookPath = (page: 'home' | 'study' | 'review' | 'note') => {
  if (page === 'home') return IYAGIGO_PATH.VOCABULARY_BOOK_HOME_PATH;
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu ||
    null) as TIyagigoSubMenu['vocabulary-book'];
  const studyLanguage = store.getState().rootReducer?.studentReducer?.currentStudyLanguage ?? null;
  const language = (studyLanguage ?? EApiLanguageCode.JAPANESE).toLowerCase();
  if (subMenu) return `/ig/vocabulary-book/${page}?language=${language}&subject=${subMenu}`;
  return IYAGIGO_PATH.VOCABULARY_BOOK_HOME_PATH;
};

// ############### test path ###############
export const createTestPath = (mode: 'home' | 'problem') => {
  if (mode === 'home') return `/ig/test/home`;
  const currentToeicTest = store.getState().rootReducer?.studentReducer?.currentToeicTest ?? null;
  const currentJlptTest = store.getState().rootReducer?.studentReducer?.currentJlptTest ?? null;
  const studyLanguage = store.getState().rootReducer?.studentReducer?.currentStudyLanguage ?? null;
  const language = (studyLanguage ?? EApiLanguageCode.JAPANESE).toLowerCase();
  const currentTest = studyLanguage === EApiLanguageCode.ENGLISH ? currentToeicTest : currentJlptTest;
  if (currentTest) return `/ig/test/problem?language=${language}&subject=${currentTest.toLowerCase()}`;
  return IYAGIGO_PATH.TEST_HOME_PATH;
};

// ############### hub path ###############
export const createHubPath = (page: 'home' | 'sub' = 'sub') => {
  if (page === 'home') return IYAGIGO_PATH.HUB_HOME_PATH;
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu || null) as TIyagigoSubMenu['hub'];

  if (subMenu) {
    return `/ig/hub/${subMenu}`;
  }

  return IYAGIGO_PATH.HUB_HOME_PATH;
};

// ############### studio path ###############
export const createStudioPath = (page: 'home' | 'sub' = 'sub') => {
  if (page === 'home') return IYAGIGO_PATH.STUDIO_HOME_PATH;
  const subMenu = (store.getState().rootReducer?.navigationReducer?.iyagigo?.subMenu ||
    null) as TIyagigoSubMenu['studio'];

  if (subMenu) {
    return `/ig/studio/${subMenu}`;
  }

  return IYAGIGO_PATH.STUDIO_HOME_PATH;
};
